import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import type { OrderStatus } from "../../../../components/common/chip";

interface SidebarProps {
  status: OrderStatus;
  totalValue: number;
  itemCount: number;
  expectedShip: string;
  currency?: string;
  /** Optional — PurchaseOrder DTO doesn't expose payment state today; renders as "—" if absent. */
  paymentStatus?: string;
  /** Optional — PO DTO has no notes field; renders fallback copy if absent. */
  deliveryNotes?: string;
  onMarkShipped?: () => void;
  onMarkDelivered?: () => void;
}

const STATUS_LABEL: Partial<Record<OrderStatus, string>> = {
  processing: "Order Placed",
  inTransit:  "In Transit",
  delivered:  "Delivered",
  validated:  "Validated",
  cancelled:  "Cancelled",
};

const STATUS_SUBTITLE: Partial<Record<OrderStatus, string>> = {
  processing: "Awaiting shipment from your end",
  inTransit:  "Shipment is on its way to the customer.",
  delivered:  "Proof submitted. Waiting for buyer to validate.",
  validated:  "Order has been validated by the buyer.",
  cancelled:  "This order was cancelled.",
};

function InfoRow({ label, value, size = "body" }: { label: string; value: string; size?: "body" | "bodySm" }) {
  return (
    <div className="flex items-center justify-between border-b border-borderLight py-2.5 first:border-t">
      <Text variant={size} tone="muted">{label}</Text>
      <Text variant={size} tone="dark">{value}</Text>
    </div>
  );
}

export function SellerOrderSidebar({ status, totalValue, itemCount, expectedShip, currency = "QAR", paymentStatus, deliveryNotes, onMarkShipped, onMarkDelivered }: SidebarProps) {
  const shipLabel = (status === "delivered" || status === "validated") ? "Delivered on" : status === "inTransit" ? "Shipped on" : "Expected Ship";

  return (
    <div className="flex flex-col gap-4">

      {/* Current Status */}
      <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
        <Text variant="bodySm" tone="muted">Current Status</Text>
        <div className="flex flex-col gap-1">
          <Heading variant="h3" tone="dark">{STATUS_LABEL[status] ?? "Unknown"}</Heading>
          <Text variant="bodySm" tone="dark">{STATUS_SUBTITLE[status] ?? ""}</Text>
        </div>
        {status === "processing" && (
          <Button variant="filledDark" size="md" fullWidth onClick={onMarkShipped}>
            Mark as Shipped
          </Button>
        )}
        {status === "inTransit" && (
          <div className="flex flex-col gap-2">
            <Button variant="filledDark" size="md" fullWidth onClick={onMarkDelivered}>
              Mark as Delivered
            </Button>
            <Text variant="bodySm" tone="muted" className="text-center">Upload proof above first</Text>
          </div>
        )}
        {status === "delivered" && (
          <Button variant="secondary" size="md" fullWidth disabled>
            Awaiting Validation
          </Button>
        )}
        {/* Validated state — upload-invoice action lives in the dedicated
            "Invoices" card on the order details page (handles seller upload,
            replace, remove, and read-only view of the auto-generated vendor
            bill). The sidebar status here is just the textual indicator. */}
      </div>

      {/* Proof on Record — delivered/validated */}
      {(status === "delivered" || status === "validated") && (
        <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
          <Heading variant="h3" tone="dark">Proof on Record</Heading>
          <div className="flex flex-col">
            <InfoRow size="bodySm" label="Delivered by"  value="Ahmed Al-Khatib (DHL)" />
            <InfoRow size="bodySm" label="Proof type"    value="Photo + Signature" />
            <InfoRow size="bodySm" label="Submitted"     value="24 Mar 2026, 10:32 AM" />
            <InfoRow size="bodySm" label="Signed by"     value="Ali Hassan" />
          </div>
        </div>
      )}

      {/* Order Summary */}
      <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
        <Heading variant="h3" tone="dark">Order Summary</Heading>
        <div className="flex flex-col">
          <InfoRow label="Total Value"    value={`${currency} ${totalValue.toLocaleString()}`} />
          <InfoRow label="Item Count"     value={`${itemCount} items`} />
          <InfoRow label={shipLabel}      value={expectedShip} />
          <InfoRow label="Payment Status" value={paymentStatus ?? "—"} />
        </div>
      </div>

      {/* Delivery Notes */}
      {status !== "delivered" && (
        <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
          <Heading variant="h3" tone="dark">Delivery Notes</Heading>
          <div className="rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
            <Text variant="bodySm" tone="muted">{deliveryNotes || "No delivery notes."}</Text>
          </div>
        </div>
      )}

    </div>
  );
}
